import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useFeatures } from '../hooks/useFeature';
// import SkillProgressTracker from './SkillProgressTracker'; // Not used currently
// import LevelProgressTracker from './LevelProgressTracker'; // Not used currently
import ProgressHistory from './ProgressHistory';
import CoachNotes from './CoachNotes';
import CertificateDisplay from './CertificateDisplay';
import GymnastRoutines from './routines/GymnastRoutines';
import { APPARATUS } from './routines/routineConstants';
import { currentLevelNumber, currentLevel as findCurrentLevel } from '../utils/currentLevel';
import GymnastDiaryTab from './diary/GymnastDiaryTab';
import SkillMatrix from './SkillMatrix';
import ScrollRow from './ui/ScrollRow';

const TAB_IDS = ['levels', 'matrix', 'routines', 'history', 'notes', 'diary'];

// Whether a skill is one of a level's skills. Uses the level's own skill list,
// so shared skills (which have no levelId of their own) count too.
const inLevel = (level, skill) => (level.skills || []).some(s => s.id === skill?.id);

// Levels without a discipline are trampoline levels
const disciplineOf = level => level.discipline || 'TRA';
const routineWord = level => (disciplineOf(level) === 'DMT' ? 'passes' : 'routines');
// Skill progress is kept per apparatus: done on trampoline isn't done on double mini
const onApparatus = (level, sp) => (sp.apparatus || 'TRA') === disciplineOf(level);

const GymnastProgress = ({ gymnastId, initialTab }) => {
  const [gymnast, setGymnast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // A change that didn't reach the server, kept on screen until it's retried
  // or dismissed so a coach never walks away thinking it saved.
  const [saveError, setSaveError] = useState(null); // { message, retry }
  const [levels, setLevels] = useState([]);
  // Every club skill, for the skill matrix (fetched when it's first opened)
  const [allSkills, setAllSkills] = useState(null);
  const [coachingMode, setCoachingMode] = useState(false);

  const [activeTab, setActiveTab] = useState(TAB_IDS.includes(initialTab) ? initialTab : 'levels');
  // Which apparatus's levels are shown: each has its own path
  const [apparatus, setApparatus] = useState('TRA');
  const [collapsedLevels, setCollapsedLevels] = useState(new Set());
  const [confirmCompleteLevel, setConfirmCompleteLevel] = useState(null);
  const [loadingSkills, setLoadingSkills] = useState(new Set());
  const [certImageUrls, setCertImageUrls] = useState({});
  const certBlobsRef = React.useRef({});
  const { user, can } = useAuth();
  const has = useFeatures();

  // Staff who manage skill tracking get the coaching tools
  const canCoach = can('skillTracking', 'MANAGE');

  useEffect(() => {
    if (activeTab !== 'matrix' || allSkills) return;
    axios.get('/api/skills').then(res => setAllSkills(Array.isArray(res.data) ? res.data : [])).catch(() => setAllSkills([]));
  }, [activeTab, allSkills]);

  // Set coaching mode to true by default for coaches and club admins
  useEffect(() => {
    if (canCoach) {
      setCoachingMode(true);
    }
  }, [canCoach]);

  // Auto-scroll to current level on mobile
  useEffect(() => {
    if (gymnast && levels.length > 0) {
      const currentLevel = findCurrentLevel(gymnast, levels);
      
      if (currentLevel) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          const currentLevelElement = document.getElementById(`level-${currentLevel.id}`);
          if (currentLevelElement) {
            currentLevelElement.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
          }
        }, 500);
      }
    }
  }, [gymnast, levels]);

  // Helper function to extract base level number from identifier
  const getBaseLevelNumber = useCallback((identifier) => {
    const match = identifier.match(/^(\d+)/);
    return match ? parseInt(match[1], 10) : 0;
  }, []);

  // Helper function to check if a level is a side-track
  const isSideTrack = useCallback((identifier) => {
    return /^\d+[a-z]$/.test(identifier);
  }, []);

  // Helper function to determine the gymnast's current working level number
  const getCurrentLevelNumber = useCallback((gymnast, levels) => {
    if (!gymnast || !levels.length) return 1;
    return currentLevelNumber(gymnast);
  }, []);

  // Helper function to check if a side-track should be available
  const isSideTrackAvailable = useCallback((level, currentLevelNumber) => {
    if (!isSideTrack(level.identifier)) {
      return true; // Regular levels are always available
    }

    const baseLevelNumber = getBaseLevelNumber(level.identifier);
    return currentLevelNumber >= baseLevelNumber;
  }, [isSideTrack, getBaseLevelNumber]);

  // Helper function to toggle level collapse
  const toggleLevelCollapse = (levelId) => {
    setCollapsedLevels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(levelId)) {
        newSet.delete(levelId);
      } else {
        newSet.add(levelId);
      }
      return newSet;
    });
  };

  // Helper function to determine which levels should be auto-collapsed
  const getAutoCollapsedLevels = useCallback((levelProgressData, currentLevelNumber) => {
    const autoCollapsed = new Set();
    levelProgressData.forEach(({ level }) => {
      const isCurrentMainTrackLevel = !isSideTrack(level.identifier) &&
                                     parseInt(level.identifier) === currentLevelNumber;
      if (!isCurrentMainTrackLevel) {
        autoCollapsed.add(level.id);
      }
    });
    return autoCollapsed;
  }, [isSideTrack]);

  // Display order: main levels still in progress, then completed main levels,
  // then optional (side-track) levels in their own section at the bottom.
  // Each apparatus is shown on its own sub-tab, so this applies within one apparatus.
  const LEVEL_GROUP = { ACTIVE: 0, COMPLETED: 1, OPTIONAL: 2 };
  const levelGroup = (level, isCompleted) => {
    if (isSideTrack(level.identifier)) return LEVEL_GROUP.OPTIONAL;
    return isCompleted ? LEVEL_GROUP.COMPLETED : LEVEL_GROUP.ACTIVE;
  };
  const LEVEL_GROUP_HEADINGS = {
    [LEVEL_GROUP.COMPLETED]: { id: 'completed-levels-section', label: 'Completed levels' },
    [LEVEL_GROUP.OPTIONAL]: { id: 'optional-levels-section', label: 'Optional levels' },
  };

  // Helper function to sort levels by identifier
  const sortLevelsByIdentifier = (a, b) => {
    const aId = a.identifier || a.number?.toString();
    const bId = b.identifier || b.number?.toString();
    
    // Extract numeric part and letter part (D3a: the number after the D)
    const aNum = parseFloat(String(aId).replace(/^\D+/, ''));
    const bNum = parseFloat(String(bId).replace(/^\D+/, ''));
    
    // If numbers are different, sort by number
    if (aNum !== bNum) {
      return aNum - bNum;
    }
    
    // If numbers are the same, sort by the full identifier (handles 3a, 3b, 3c)
    return aId.localeCompare(bId);
  };

  const handleProgressUpdate = async () => {
    try {
      const [gymnastResponse, levelsResponse] = await Promise.all([
        axios.get(`/api/gymnasts/${gymnastId}`),
        axios.get('/api/levels')
      ]);

      setGymnast(gymnastResponse.data);
      setLevels(levelsResponse.data);
      // Preserve the user's current expand/collapse state — do not re-collapse levels
    } catch (error) {
      console.error('Failed to refresh progress:', error);
    }
  };

  const failedSave = (what, err, retry) => {
    const reason = err.response
      ? (err.response.data?.error || 'the server returned an error')
      : 'there was no signal';
    setSaveError({ message: `${what} wasn't saved because ${reason}.`, retry });
  };

  const nameOf = (key, id) => levels.flatMap(l => l[key] || []).find(x => x.id === id)?.name;

  const handleSkillStatusChange = async (skillId, status, apparatus = 'TRA') => {
    setLoadingSkills(prev => new Set(prev).add(skillId));
    try {
      await axios.post(`/api/progress/skill`, { gymnastId, skillId, status, apparatus });
      setSaveError(null);
      handleProgressUpdate();
    } catch (error) {
      console.error('Error updating skill status:', error);
      failedSave(nameOf('skills', skillId) || 'That skill', error, () => handleSkillStatusChange(skillId, status, apparatus));
    } finally {
      setLoadingSkills(prev => {
        const next = new Set(prev);
        next.delete(skillId);
        return next;
      });
    }
  };

  const handleRoutineStatusChange = async (routineId, status) => {
    try {
      await axios.put(`/api/progress/routine`, {
        gymnastId,
        routineId,
        status
      });
      setSaveError(null);

      // Refresh data
      handleProgressUpdate();
    } catch (error) {
      console.error('Error updating routine status:', error);
      failedSave(nameOf('routines', routineId) || 'That routine', error, () => handleRoutineStatusChange(routineId, status));
    }
  };

  const handleCompleteLevel = (levelId) => {
    // Find the level to get its details for confirmation
    const level = levels.find(l => l.id === levelId);
    if (level) {
      setConfirmCompleteLevel(level);
    }
  };

  const confirmAndCompleteLevel = async (level = confirmCompleteLevel) => {
    if (!level) return;
    
    try {
      const response = await axios.post(`/api/progress/level/${level.id}/complete`, {
        gymnastId
      });
      setSaveError(null);
      
      // Show success message
      console.log(response.data.message);
      
      // Refresh data to show updated progress
      handleProgressUpdate();
      
      // Close confirmation dialog
      setConfirmCompleteLevel(null);
    } catch (error) {
      console.error('Error completing level:', error);
      failedSave(`Completing ${level.name}`, error, () => confirmAndCompleteLevel(level));
      setConfirmCompleteLevel(null);
    }
  };

  // Fetch certificate PNG blob URLs for completed levels
  useEffect(() => {
    if (!gymnast?.certificates?.length) return;
    const prev = certBlobsRef.current;
    Promise.all(
      gymnast.certificates.map(async cert => {
        if (prev[cert.id]) return { id: cert.id, url: prev[cert.id] };
        try {
          const res = await axios.get(`/api/certificates/${cert.id}/preview`, { responseType: 'blob' });
          return { id: cert.id, url: URL.createObjectURL(res.data) };
        } catch {
          return { id: cert.id, url: null };
        }
      })
    ).then(results => {
      const map = {};
      results.forEach(r => { if (r.url) map[r.id] = r.url; });
      certBlobsRef.current = map;
      setCertImageUrls(map);
    });
    return () => {
      Object.values(certBlobsRef.current).forEach(url => url && URL.revokeObjectURL(url));
    };
  }, [gymnast?.certificates]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [gymnastResponse, levelsResponse] = await Promise.all([
          axios.get(`/api/gymnasts/${gymnastId}`),
          axios.get('/api/levels')
        ]);
        
        setGymnast(gymnastResponse.data);
        setLevels(levelsResponse.data);
        
        // Auto-collapse levels on initial load
        if (gymnastResponse.data && levelsResponse.data.length > 0) {
          const currentLevelNum = getCurrentLevelNumber(gymnastResponse.data, levelsResponse.data);
          const availableLvls = levelsResponse.data.filter(level => 
            isSideTrackAvailable(level, currentLevelNum)
          );

          const levelProgressData = availableLvls.map(level => {
            const completedSkills = gymnastResponse.data.skillProgress
              .filter(sp => sp.status === 'COMPLETED' && inLevel(level, sp.skill) && onApparatus(level, sp))
              .map(sp => sp.skill);

            const totalSkills = level.skills ? level.skills.length : 0;
            const completedCount = completedSkills.length;
            const progressPercentage = totalSkills > 0 ? (completedCount / totalSkills) * 100 : 0;
            
            return {
              level,
              progressPercentage
            };
          });

          const autoCollapsed = getAutoCollapsedLevels(levelProgressData, currentLevelNum);
          setCollapsedLevels(autoCollapsed);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        setError('Failed to load gymnast data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [gymnastId, getAutoCollapsedLevels, getCurrentLevelNumber, isSideTrackAvailable]);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        {error}
      </div>
    );
  }

  if (!gymnast) {
    return (
      <div className="alert alert-info">
        No gymnast data available.
      </div>
    );
  }

  // The gymnast viewing their own record can enter routines for a coach to check.
  const isSelf = Boolean(gymnast.userId) && gymnast.userId === user?.id;

  // Process level progress data
  const nextMainLevelNumber = getCurrentLevelNumber(gymnast, levels);
  // Routines can pick an apparatus the club has no levels for; levels then stay on trampoline
  const levelApparatus = levels.some(l => disciplineOf(l) === apparatus) ? apparatus : 'TRA';
  
  // Filter levels based on side-track availability
  const availableLevels = levels.filter(level =>
    disciplineOf(level) === levelApparatus && isSideTrackAvailable(level, nextMainLevelNumber)
  );

  const levelProgress = availableLevels.map(level => {
    const completedSkills = gymnast.skillProgress
      .filter(sp => sp.status === 'COMPLETED' && inLevel(level, sp.skill) && onApparatus(level, sp))
      .map(sp => sp.skill);

    const totalSkills = level.skills ? level.skills.length : 0;
    const completedCount = completedSkills.length;
    const progressPercentage = totalSkills > 0 ? (completedCount / totalSkills) * 100 : 0;

    // Check if level is completed
    const isCompleted = gymnast.levelProgress
      .some(lp => lp.level?.id === level.id && lp.status === 'COMPLETED');

    return {
      level,
      completedSkills,
      totalSkills,
      completedCount,
      isCompleted,
      progressPercentage
    };
  }).sort((a, b) => {
    const groupDiff = levelGroup(a.level, a.isCompleted) - levelGroup(b.level, b.isCompleted);
    if (groupDiff !== 0) return groupDiff;
    return sortLevelsByIdentifier(a.level, b.level);
  });

  // Get current and working levels
  // const completedLevels = gymnast.levelProgress
  //   .filter(lp => lp.status === 'COMPLETED')
  //   .map(lp => lp.level)
  //   .sort(sortLevelsByIdentifier); // Not used currently

  // Age in whole years for the header, or null without a date of birth
  const age = (() => {
    if (!gymnast.dateOfBirth) return null;
    const dob = new Date(gymnast.dateOfBirth);
    const now = new Date();
    let years = now.getFullYear() - dob.getFullYear();
    if (now < new Date(now.getFullYear(), dob.getMonth(), dob.getDate())) years -= 1;
    return years;
  })();

  // Coaches get tabs; families see a single read-only page (routines + levels).
  const TABS = [
    { id: 'levels', label: 'Levels' },
    { id: 'routines', label: 'Routines' },
    ...(has('diary') ? [{ id: 'diary', label: 'Diary' }] : []),
    { id: 'notes', label: 'Coach Notes' },
    { id: 'history', label: 'Progress History' },
    { id: 'matrix', label: 'Skill Matrix' },
  ];
  const showLevels = !canCoach || activeTab === 'levels';

  // Apparatus sub-tabs, for each apparatus the club has levels for
  const apparatusTabs = APPARATUS.filter(a => a.value === 'TRA' || levels.some(l => disciplineOf(l) === a.value));
  const chooseApparatus = (value) => {
    setApparatus(value);
    // Open the first level not yet completed on that apparatus
    const firstOpen = levels
      .filter(l => disciplineOf(l) === value && !isSideTrack(l.identifier))
      .sort(sortLevelsByIdentifier)
      .find(l => !gymnast.levelProgress.some(lp => lp.level?.id === l.id && lp.status === 'COMPLETED'));
    if (value !== 'TRA' && firstOpen) {
      setCollapsedLevels(prev => { const next = new Set(prev); next.delete(firstOpen.id); return next; });
    }
  };
  // Routines get the same sub-tabs, on the same apparatus as the levels
  const routineTabs = { apparatusTabs: apparatusTabs.map(a => a.value), apparatus, onApparatusChange: setApparatus };
  const apparatusBar = showLevels && apparatusTabs.length > 1 && (
    <ScrollRow className="gymnast-tabs gymnast-tabs--apparatus" role="tablist" aria-label="Apparatus" selected={levelApparatus}>
      {apparatusTabs.map(a => (
        <button
          key={a.value}
          type="button"
          role="tab"
          aria-selected={levelApparatus === a.value}
          className={`gymnast-tabs__tab${levelApparatus === a.value ? ' active' : ''}`}
          onClick={() => chooseApparatus(a.value)}
        >
          {a.label}
        </button>
      ))}
    </ScrollRow>
  );
  // Rendered in both the desktop and mobile layouts (only one is visible).
  const tabBar = canCoach && (
    <ScrollRow className="gymnast-tabs" role="tablist" aria-label="Gymnast sections" selected={activeTab}>
      {TABS.map(tab => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={activeTab === tab.id}
          className={`gymnast-tabs__tab${activeTab === tab.id ? ' active' : ''}`}
          onClick={() => setActiveTab(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </ScrollRow>
  );

  return (
    <div className="gymnast-progress">

      {/* Parent explainer — shown for non-coaches */}
      {!canCoach && (
        <div className="progress-parent-explainer">
          <p>
            Your coach marks skills off during sessions. Skills are grouped into levels —
            starting at Level 1 and working upwards, each one introducing new skills and
            techniques that build on what came before.
          </p>
        </div>
      )}

      {/* Desktop Layout */}
      <div className="desktop-layout">
        {/* Desktop Progress Summary */}
        <div className="progress-summary desktop-progress-summary">
          <div className="progress-header">
            <h3>
              {gymnast.firstName} {gymnast.lastName}
              {age != null && <span className="gymnast-age">Age {age}</span>}
            </h3>
            {canCoach && (
              <div className="coaching-controls">
                <button
                  onClick={() => setCoachingMode(!coachingMode)}
                  className={`btn ${coachingMode ? 'btn-success' : 'btn-outline'}`}
                >
                  {coachingMode ? 'Exit Coaching Mode' : 'Enter Coaching Mode'}
                </button>
              </div>
            )}
          </div>
        </div>

        {tabBar}

        {/* Routines above levels for families; coaches use the Routines tab */}
        {!canCoach && <GymnastRoutines gymnastId={gymnastId} canEdit={false} canSubmit={isSelf && has('gymnastRoutines')} {...routineTabs} />}

        {apparatusBar}

        {/* Desktop Level Progress Overview */}
        {showLevels && (
          <div className="card desktop-level-progress">
            <div className="card-header">
              <h4 className="card-title">Level Progress</h4>
              <div className="collapse-info">
                <small className="text-muted">
                  💡 Only the current level is expanded by default. Click ▼/▲ to expand/collapse.
                </small>
              </div>
            </div>
            <div className="level-progress-list">
              {levelProgress.map(({ level, completedSkills, totalSkills, completedCount, isCompleted, progressPercentage }, idx) => {
                const group = levelGroup(level, isCompleted);
                const prev = levelProgress[idx - 1];
                const heading = (!prev || levelGroup(prev.level, prev.isCompleted) !== group) && LEVEL_GROUP_HEADINGS[group];
                // Get routine progress for this level
                const levelRoutines = level.routines || [];
                const routineProgress = gymnast.routineProgress?.filter(rp => 
                  levelRoutines.some(lr => lr.id === rp.routine.id)
                ) || [];

                const isCollapsed = collapsedLevels.has(level.id);
                const levelClass = `level-progress-item ${isCompleted ? 'completed' : ''} ${isSideTrack(level.identifier) ? 'side-track-level' : ''}`;

                return (
                  <React.Fragment key={level.id}>
                  {heading && <h5 id={heading.id} className="level-group-heading">{heading.label}</h5>}
                  <div id={`level-${level.id}`} className={levelClass}>
                    <div className="level-header">
                      <div className="level-info">
                        <div className="level-title-row">
                          <h5>
                            {level.identifier} - {level.name}
                            {isSideTrack(level.identifier) && (
                              <span className="side-track-badge">Optional</span>
                            )}
                          </h5>
                        </div>
                        <div className="level-stats">
                          <span className="progress-text">
                            {completedCount} of {totalSkills} skills completed
                          </span>
                          {isCompleted && (
                            <span className="badge badge-success">Level Completed</span>
                          )}
                          {isCompleted && !isCollapsed && (() => {
                            const cert = gymnast.certificates?.find(c => c.levelId === level.id);
                            const url = cert && certImageUrls[cert.id];
                            return url ? (
                              <a href={url} download={`certificate-level-${level.identifier}.png`} className="level-cert-link">
                                🏆 View certificate
                              </a>
                            ) : null;
                          })()}
                        </div>
                      </div>
                      <div className="progress-bar-container">
                        <div className="progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{ width: `${progressPercentage}%` }}
                          />
                        </div>
                        <span className="progress-percentage">{Math.round(progressPercentage)}%</span>

                        {canCoach && coachingMode && !isCompleted && (totalSkills > 0 || level.routines?.length > 0) && (
                          <button
                            onClick={() => handleCompleteLevel(level.id)}
                            className="btn btn-xs btn-success"
                            title={`Complete all skills and ${routineWord(level)} in this level`}
                            style={{ marginRight: '5px' }}
                          >
                            Complete Level
                          </button>
                        )}

                        <button
                            onClick={() => toggleLevelCollapse(level.id)}
                            className="btn btn-xs btn-outline collapse-toggle"
                            title={isCollapsed ? 'Expand level' : 'Collapse level'}
                          >
                            {isCollapsed ? '▼' : '▲'}
                          </button>
                      </div>
                    </div>
                    
                    {!isCollapsed && (
                      <div className="level-content">
                        {level.skills && level.skills.length > 0 && (
                          <div className="level-skills">
                            <h6>Skills:</h6>
                            <div className="skills-grid">
                              {level.skills.map(skill => {
                                const skillProgress = gymnast.skillProgress.find(sp => sp.skill.id === skill.id && onApparatus(level, sp));
                                const currentStatus = skillProgress?.status || 'NOT_STARTED';
                                
                                const isSkillLoading = loadingSkills.has(skill.id);
                                return (
                                  <div key={skill.id} className={`skill-card skill-${currentStatus.toLowerCase()}${isSkillLoading ? ' skill-loading' : ''}`}>
                                    <div className="skill-name">{skill.name}</div>
                                    {canCoach && coachingMode && (
                                      <div className="skill-controls">
                                        <button
                                          onClick={() => handleSkillStatusChange(skill.id, 'NOT_STARTED', disciplineOf(level))}
                                          className={`btn btn-xs ${currentStatus === 'NOT_STARTED' ? 'btn-secondary' : 'btn-outline'}`}
                                          title="Not Started"
                                          disabled={isSkillLoading}
                                        >
                                          ○
                                        </button>
                                        <button
                                          onClick={() => handleSkillStatusChange(skill.id, 'IN_PROGRESS', disciplineOf(level))}
                                          className={`btn btn-xs ${currentStatus === 'IN_PROGRESS' ? 'btn-warning' : 'btn-outline'}`}
                                          title="In Progress"
                                          disabled={isSkillLoading}
                                        >
                                          ◐
                                        </button>
                                        <button
                                          onClick={() => handleSkillStatusChange(skill.id, 'COMPLETED', disciplineOf(level))}
                                          className={`btn btn-xs ${currentStatus === 'COMPLETED' ? 'btn-success' : 'btn-outline'}`}
                                          title="Completed"
                                          disabled={isSkillLoading}
                                        >
                                          ✓
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Routine Section */}
                        {levelRoutines.length > 0 && (
                          <div className="level-routines">
                            <h6>Routine:</h6>
                            {levelRoutines.map(routine => {
                              const routineProgressData = routineProgress.find(rp => rp.routine.id === routine.id);
                              const routineStatus = routineProgressData?.status || 'NOT_STARTED';
                              const routineSkills = routine.routineSkills || [];
                              
                              return (
                                <div key={routine.id} className={`routine-card routine-${routineStatus.toLowerCase()}`}>
                                  <div className="routine-header">
                                    <div className="routine-info">
                                      <div className="routine-name">
                                        {routine.name || `Level ${level.identifier} Routine`}
                                      </div>
                                      <div className="routine-status">
                                        {routineStatus === 'COMPLETED' && (
                                          <span className="badge badge-success">Routine Completed</span>
                                        )}
                                        {routineStatus === 'NOT_STARTED' && (
                                          <span className="badge badge-secondary">Not Started</span>
                                        )}
                                      </div>
                                    </div>
                                    {canCoach && coachingMode && (
                                      <div className="routine-controls">
                                        <button
                                          onClick={() => handleRoutineStatusChange(routine.id, 'NOT_STARTED')}
                                          className={`btn btn-sm ${routineStatus === 'NOT_STARTED' ? 'btn-secondary' : 'btn-outline'}`}
                                          title="Not Started"
                                        >
                                          Not Started
                                        </button>
                                        <button
                                          onClick={() => handleRoutineStatusChange(routine.id, 'COMPLETED')}
                                          className={`btn btn-sm ${routineStatus === 'COMPLETED' ? 'btn-success' : 'btn-outline'}`}
                                          title="Completed"
                                        >
                                          Completed
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {routineSkills.length > 0 && (
                                    <div className="routine-skills">
                                      <h6>Required Skills:</h6>
                                      <div className="routine-skills-list">
                                        {routineSkills.map(routineSkill => (
                                          <div key={routineSkill.id} className="routine-skill-item">
                                            <span className="routine-skill-name">
                                              {routineSkill.skill ? routineSkill.skill.name : routineSkill.customSkillName}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* Certificates Section - Show for non-coaches */}
        {!coachingMode && showLevels && has('certificates') && (
          <div className="card" id="certificates-section">
            <CertificateDisplay
              gymnastId={gymnastId}
              showActions={false}
            />
          </div>
        )}

        {/* Guardians */}
        {showLevels && gymnast.guardians && gymnast.guardians.length > 0 && (
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">Guardians</h4>
            </div>
            <div className="guardian-list">
              {gymnast.guardians.map(guardian => (
                <div key={guardian.id} className="guardian-item">
                  <strong>{guardian.firstName} {guardian.lastName}</strong>
                  <span className="text-muted">{guardian.email}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Layout */}
      <div className="mobile-layout">

        {/* Mobile-First Progress Interface */}
        <>
          {/* Mobile Header with Quick Actions */}
          <div className="mobile-progress-header">
            <h2 className="gymnast-name-mobile">{gymnast.firstName} {gymnast.lastName}</h2>
            {age != null && <span className="gymnast-age">Age {age}</span>}
          </div>

          {tabBar}

          {showLevels && (
          <>
          {/* Certificates shortcut */}
          {!canCoach && gymnast.certificates?.length > 0 && (
            <button
              className="progress-cert-shortcut"
              onClick={() => document.getElementById('completed-levels-section')?.scrollIntoView({ behavior: 'smooth' })}
            >
              🏆 View certificates
            </button>
          )}

          {/* Routines above levels for families; coaches use the Routines tab */}
          {!canCoach && <GymnastRoutines gymnastId={gymnastId} canEdit={false} canSubmit={isSelf && has('gymnastRoutines')} {...routineTabs} />}

          {apparatusBar}

          {/* Mobile Level Cards */}
          <div className="mobile-levels-container">
            {levels
              .filter(level => disciplineOf(level) === levelApparatus && isSideTrackAvailable(level, nextMainLevelNumber))
              .sort((a, b) => {
                const aCompleted = gymnast.levelProgress.some(lp => lp.level?.id === a.id && lp.status === 'COMPLETED');
                const bCompleted = gymnast.levelProgress.some(lp => lp.level?.id === b.id && lp.status === 'COMPLETED');
                const groupDiff = levelGroup(a, aCompleted) - levelGroup(b, bCompleted);
                if (groupDiff !== 0) return groupDiff;
                return sortLevelsByIdentifier(a, b);
              })
              .map((level, idx, arr) => {
              // Calculate progress for each level (including side tracks)
              const completedSkills = gymnast.skillProgress
                .filter(sp => sp.status === 'COMPLETED' && inLevel(level, sp.skill) && onApparatus(level, sp))
                .map(sp => sp.skill);

              const totalSkills = level.skills ? level.skills.length : 0;
              const completedCount = completedSkills.length;
              const progressPercentage = totalSkills > 0 ? (completedCount / totalSkills) * 100 : 0;

              // Check if level is completed
              const isCompleted = gymnast.levelProgress
                .some(lp => lp.level?.id === level.id && lp.status === 'COMPLETED');
              const isCollapsed = collapsedLevels.has(level.id);
              const isCurrentLevel = !isSideTrack(level.identifier) && parseInt(level.identifier) === getCurrentLevelNumber(gymnast, levels);

              const prevLevel = idx > 0 ? arr[idx - 1] : null;
              const prevCompleted = prevLevel ? gymnast.levelProgress.some(lp => lp.level?.id === prevLevel.id && lp.status === 'COMPLETED') : false;
              const group = levelGroup(level, isCompleted);
              const heading = (!prevLevel || levelGroup(prevLevel, prevCompleted) !== group) && LEVEL_GROUP_HEADINGS[group];

              return (
                <React.Fragment key={level.id}>
                {heading && (
                  <div id={heading.id} className="mobile-levels-divider">{heading.label}</div>
                )}
                <div id={`level-${level.id}`} className={`mobile-level-card ${isCompleted ? 'completed' : ''} ${isCurrentLevel ? 'current' : ''} ${isSideTrack(level.identifier) ? 'side-track' : ''}`}>
                  {/* Level Header */}
                  <div className="mobile-level-header" onClick={() => toggleLevelCollapse(level.id)}>
                    <div className="mobile-level-title-row">
                      <span className="level-identifier">{level.identifier}</span>
                      <span className="level-name">{level.name}</span>
                      {isSideTrack(level.identifier) && <span className="side-track-indicator">Optional</span>}
                      {isCompleted ? (
                        <span className="status-badge completed">✓ Done</span>
                      ) : isCurrentLevel ? (
                        <span className="status-badge current">Current</span>
                      ) : null}
                    </div>
                    <div className="mobile-level-progress-row">
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${progressPercentage}%` }} />
                      </div>
                      <span className="progress-text">{completedCount}/{totalSkills}</span>
                      <span className="expand-indicator">{isCollapsed ? '▼' : '▲'}</span>
                    </div>
                  </div>

                  {/* Certificate image for completed levels, once expanded */}
                  {isCompleted && !isCollapsed && (() => {
                    const cert = gymnast.certificates?.find(c => c.levelId === level.id);
                    const url = cert && certImageUrls[cert.id];
                    return url ? (
                      <div className="mobile-level-cert">
                        <img src={url} alt="Certificate" className="mobile-level-cert__img" />
                        <a href={url} download={`certificate-level-${level.identifier}.png`} className="mobile-level-cert__download">
                          Download certificate
                        </a>
                      </div>
                    ) : null;
                  })()}

                  {/* Level Content */}
                  {!isCollapsed && (
                    <div className="mobile-level-content">

                      {/* Skills List */}
                      {level.skills && level.skills.length > 0 && (
                        <div className="mobile-skills-section">
                          <div className="skills-header">
                            <h4>Skills ({completedCount}/{totalSkills})</h4>
                            {canCoach && coachingMode && !isCompleted && totalSkills > 0 && (
                              <button
                                onClick={() => handleCompleteLevel(level.id)}
                                className="btn btn-success btn-sm complete-level-btn"
                              >
                                Complete Level
                              </button>
                            )}
                          </div>
                          
                          <div className="mobile-skills-list">
                            {level.skills.map(skill => {
                              const skillProgress = gymnast.skillProgress.find(sp => sp.skill.id === skill.id && onApparatus(level, sp));
                              const currentStatus = skillProgress?.status || 'NOT_STARTED';
                              
                              const isSkillLoading = loadingSkills.has(skill.id);
                              return (
                                <div key={skill.id} className={`mobile-skill-item ${currentStatus.toLowerCase()}${isSkillLoading ? ' skill-loading' : ''}`}>
                                  <div className="skill-name-mobile">{skill.name}</div>

                                  {canCoach && coachingMode && (
                                    <div className="skill-actions-mobile">
                                      <button
                                        onClick={() => handleSkillStatusChange(skill.id, 'NOT_STARTED', disciplineOf(level))}
                                        className={`status-btn not-started ${currentStatus === 'NOT_STARTED' ? 'active' : ''}`}
                                        disabled={isSkillLoading}
                                      >
                                        ○
                                      </button>
                                      <button
                                        onClick={() => handleSkillStatusChange(skill.id, 'IN_PROGRESS', disciplineOf(level))}
                                        className={`status-btn in-progress ${currentStatus === 'IN_PROGRESS' ? 'active' : ''}`}
                                        disabled={isSkillLoading}
                                      >
                                        ◐
                                      </button>
                                      <button
                                        onClick={() => handleSkillStatusChange(skill.id, 'COMPLETED', disciplineOf(level))}
                                        className={`status-btn completed ${currentStatus === 'COMPLETED' ? 'active' : ''}`}
                                        disabled={isSkillLoading}
                                      >
                                        ✓
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Routine Section for Mobile */}
                      {(() => {
                        const levelRoutines = level.routines || [];
                        const routineProgress = gymnast.routineProgress?.filter(rp => 
                          levelRoutines.some(lr => lr.id === rp.routine.id)
                        ) || [];

                        return levelRoutines.length > 0 && (
                          <div className="mobile-routine-section">
                            <div className="routine-header">
                              <h4>Routine ({levelRoutines.length})</h4>
                            </div>
                            
                            <div className="mobile-routine-list">
                              {levelRoutines.map(routine => {
                                const routineProgressData = routineProgress.find(rp => rp.routine.id === routine.id);
                                const routineStatus = routineProgressData?.status || 'NOT_STARTED';
                                const routineSkills = routine.routineSkills || [];
                                
                                return (
                                  <div key={routine.id} className={`mobile-routine-item ${routineStatus.toLowerCase()}`}>
                                    <div className="routine-main-info">
                                      <div className="routine-name-mobile">
                                        {routine.name || `Level ${level.identifier} Routine`}
                                      </div>
                                      <div className="routine-status-mobile">
                                        <span className={`routine-status-badge ${routineStatus.toLowerCase()}`}>
                                          {routineStatus === 'COMPLETED' && '✓ Complete'}
                                          {routineStatus === 'NOT_STARTED' && '○ Not Started'}
                                        </span>
                                      </div>
                                    </div>
                                    
                                    {canCoach && coachingMode && (
                                      <div className="routine-actions-mobile">
                                        <button
                                          onClick={() => handleRoutineStatusChange(routine.id, 'NOT_STARTED')}
                                          className={`routine-status-btn not-started ${routineStatus === 'NOT_STARTED' ? 'active' : ''}`}
                                        >
                                          ○
                                        </button>
                                        <button
                                          onClick={() => handleRoutineStatusChange(routine.id, 'COMPLETED')}
                                          className={`routine-status-btn completed ${routineStatus === 'COMPLETED' ? 'active' : ''}`}
                                        >
                                          ✓
                                        </button>
                                      </div>
                                    )}

                                    {routineSkills.length > 0 && (
                                      <div className="routine-skills-mobile">
                                        <div className="routine-skills-label">Required skills:</div>
                                        <ul className="routine-skills-list">
                                          {routineSkills.map(rs => (
                                            <li key={rs.id}>
                                              {rs.skill ? rs.skill.name : rs.customSkillName}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}

                    </div>
                  )}
                </div>
                </React.Fragment>
              );
            })}
          </div>
          </>
          )}
        </>

      </div>

      {/* Non-level tabs render once, below whichever layout's header and tab bar is visible */}
      {canCoach && activeTab === 'routines' && (
        <GymnastRoutines gymnastId={gymnastId} canEdit collapsible={false} {...routineTabs} />
      )}

      {canCoach && activeTab === 'matrix' && (
        <SkillMatrix
          levels={levels}
          skills={allSkills || []}
          gymnast={gymnast}
          apparatus={levelApparatus}
          apparatusTabs={apparatusTabs}
          onApparatusChange={setApparatus}
          canEdit={coachingMode}
          loadingSkills={loadingSkills}
          onStatusChange={handleSkillStatusChange}
        />
      )}

      {canCoach && activeTab === 'history' && (
        <ProgressHistory gymnastId={gymnastId} />
      )}

      {canCoach && activeTab === 'notes' && (
        <div className="card coach-notes-section">
          <CoachNotes gymnast={gymnast} onNotesUpdate={setGymnast} />
        </div>
      )}

      {canCoach && activeTab === 'diary' && (
        <GymnastDiaryTab gymnastId={gymnastId} />
      )}

      {/* Staff who can only view skill tracking get the family view (no tabs), plus the diary */}
      {!canCoach && can('skillTracking') && has('diary') && (
        <GymnastDiaryTab gymnastId={gymnastId} />
      )}

      {/* Complete Level Confirmation Modal */}
      {confirmCompleteLevel && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h4>Complete Level {confirmCompleteLevel.identifier}?</h4>
            <p>
              This will mark <strong>all skills{confirmCompleteLevel.routines?.length > 0 && ` and ${routineWord(confirmCompleteLevel)}`}</strong> in
              "{confirmCompleteLevel.name}" as completed{confirmCompleteLevel.routines?.some(r => r.isAlternative) && ' (alternatives are left as they are)'}.
              This action cannot be undone easily.
            </p>
            <p>
              Are you sure you want to complete this entire level for {gymnast.firstName} {gymnast.lastName}?
            </p>
            <div className="modal-actions">
              <button 
                onClick={() => setConfirmCompleteLevel(null)}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button 
                onClick={() => confirmAndCompleteLevel()}
                className="btn btn-success"
              >
                Yes, Complete Level
              </button>
            </div>
          </div>
        </div>
      )}

      {saveError && (
        <div className="save-error" role="alert">
          <span>{saveError.message}</span>
          <span className="save-error__actions">
            <button type="button" className="save-error__retry" onClick={() => { const { retry } = saveError; setSaveError(null); retry(); }}>
              Try again
            </button>
            <button type="button" className="save-error__close" aria-label="Dismiss" onClick={() => setSaveError(null)}>×</button>
          </span>
        </div>
      )}
    </div>
  );
};

export default GymnastProgress;
